import type { LocationGambitDto } from '@/core/data/gambit/gambit.interface';
import type { AuthorDto, GeoCoords, SearchLocationDto } from '@/core/data/location/location.interface';
import {
  LocationChessFeature,
  LocationType,
  MapLocationSource,
  MapLocationType,
} from '@/core/data/location/location.type';
import { gambits } from '@/core/gambits';
import { useMapStore } from '@/stores/map.store';
import { useTableStore } from '@/stores/table.store';
import { UnitSystem } from '@/views/components/gam-player/helpers';
import { GamChipSize, GamChipVariant } from '@/views/composables/constants/components/gamChip.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import { GamMapPinLocation, GamMapPinVariant } from '@/views/composables/constants/components/gamMap.constants';
import type { GamChipType } from '@/views/composables/models/components/GamChip';
import type { GamDropdownItem } from '@/views/composables/models/components/GamDropdown';
import { isEqual } from 'lodash';
import { storeToRefs } from 'pinia';
import { CustomMarker } from 'vue3-google-map';

const toRadian = (degree: number): number => (degree * Math.PI) / 180;

/**
 * Haversine distance using Earth's mean radius (meters). Matches ST_DistanceSphere.
 */
export const haversineDistance = (point1?: GeoCoords | null, point2?: GeoCoords | null): number => {
  if (!point1 || !point2) return 0;

  const earthRadiusInMeters = 6371008;
  const lat1 = toRadian(point1.lat);
  const lat2 = toRadian(point2.lat);
  const deltaLat = toRadian(point2.lat - point1.lat);
  const deltaLon = toRadian(point2.lng - point1.lng);

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return Math.round(earthRadiusInMeters * c);
};

function kilometersToMiles(distanceInKilometers: number) {
  return distanceInKilometers * 0.6213711922;
}

function metersToYards(distanceInMeters: number) {
  return distanceInMeters * 1.0936133;
}

export const formatDistance = (distance: number, units: UnitSystem = UnitSystem.METRIC): (string | number)[] => {
  let values = [];

  const minorUnitDistance = units === UnitSystem.IMPERIAL ? metersToYards(distance) : distance;
  const majorUnitDistance = units === UnitSystem.IMPERIAL ? kilometersToMiles(distance / 1000) : distance / 1000;

  if (majorUnitDistance < 1) {
    if (units === UnitSystem.IMPERIAL) {
      values = [Math.round(minorUnitDistance), 'yd'];
    } else {
      values = [Math.round(minorUnitDistance), 'm'];
    }
  } else if (majorUnitDistance < 10) {
    if (units === UnitSystem.IMPERIAL) {
      values = [majorUnitDistance.toPrecision(2), 'mi'];
    } else {
      values = [majorUnitDistance.toPrecision(2), 'km'];
    }
  } else {
    if (units === UnitSystem.IMPERIAL) {
      values = [Math.round(majorUnitDistance), 'mi'];
    } else {
      values = [Math.round(majorUnitDistance), 'km'];
    }
  }

  return values;
};

export interface LocationAddressOptions {
  name: string;
  address: string;
}

export const formatAddress = (location: LocationAddressOptions) => {
  const parts = location.address.split(' ');
  const cityAndCountry = [parts.pop(), parts.pop()].reverse().join(' ');

  const name = location.address.split(',')[0];

  if (name === location.name) {
    return cityAndCountry;
  }

  return `${name}, ${cityAndCountry}`;
};

export default class LocationHelper {
  static toDMS(coordinate: number, isLatitude: boolean) {
    // Determine direction
    const absolute = Math.abs(coordinate);
    const degrees = Math.floor(absolute);
    const minutesNotTruncated = (absolute - degrees) * 60;
    const minutes = Math.floor(minutesNotTruncated);
    const seconds = ((minutesNotTruncated - minutes) * 60).toFixed(2);

    const direction = coordinate >= 0 ? (isLatitude ? 'N' : 'E') : isLatitude ? 'S' : 'W';

    return `${degrees}° ${minutes}' ${seconds}" ${direction}`;
  }

  static getLocationIcon(location: GamMapPinLocation): GamIconName {
    switch (location) {
      case GamMapPinLocation.CAFE:
        return GamIconName.COFFEE;
      case GamMapPinLocation.STREET:
        return GamIconName.STREET_LOCATION;
      case GamMapPinLocation.GIANT:
        return GamIconName.GIANT_CHESS;
      case GamMapPinLocation.CLUB:
        return GamIconName.CLUB;
      case GamMapPinLocation.FAVORITE:
        return GamIconName.HEART;
      case GamMapPinLocation.EVENT:
        return GamIconName.GAMBIT;
      case GamMapPinLocation.HOME:
        return GamIconName.HOUSE;
      default:
        return GamIconName.PAWN;
    }
  }

  static getLocationTypeIcon(type?: LocationType, isPrivate?: boolean, isFavorite?: boolean): GamIconName {
    if (isPrivate && type !== LocationType.CITY) {
      return GamIconName.HOUSE;
    } else if (isFavorite) {
      return GamIconName.HEART;
    } else if (type) {
      switch (type) {
        case LocationType.CITY:
          return GamIconName.LOCATION;
        case LocationType.CAFE_CHESS:
          return GamIconName.COFFEE;
        case LocationType.VENUE:
          return GamIconName.VENUE;
        case LocationType.FAVORITE:
          return GamIconName.HEART;
        case LocationType.CHESS_FRIENDLY:
          return GamIconName.PAWN;
        case LocationType.GAMBIT:
          return GamIconName.GAMBIT;
        case LocationType.NATURE:
          return GamIconName.STREET_LOCATION;

        default:
          return GamIconName.LOCATION;
      }
    }
    return GamIconName.PAWN;
  }

  static getFeatureTypeIcon(type: LocationChessFeature, isPrivate?: boolean, isFavorite?: boolean): GamIconName {
    if (isPrivate) {
      return GamIconName.HOUSE;
    } else if (isFavorite) {
      return GamIconName.HEART;
    } else if (type) {
      switch (type) {
        case LocationChessFeature.CHESS_SET:
          return GamIconName.CHESS_SET;
        case LocationChessFeature.CHESS_TABLE:
          return GamIconName.CHESS_TABLE;
        case LocationChessFeature.GIANT_CHESS:
          return GamIconName.GIANT_CHESS;
        case LocationChessFeature.CHESS_CLOCK:
          return GamIconName.CHESS_CLOCK;
        default:
          0;
      }
    }
    return GamIconName.PAWN;
  }

  static getPinType(type: MapLocationType, visitorCount?: number, nextGambits?: LocationGambitDto[] | null): string {
    if (type !== MapLocationType.LOCATION) {
      return GamMapPinVariant.GAMBIT;
    }

    if (Number(nextGambits?.length) > 0 || Number(visitorCount) > 0) {
      return GamMapPinVariant.HAS_GAMBIT;
    }

    return GamMapPinVariant.DEFAULT;
  }

  static getBaseLocationTag(label: string, isSmall: boolean, icon?: GamIconName): GamChipType {
    return {
      label,
      size: isSmall ? GamChipSize.SMALL : GamChipSize.NORMAL,
      variant: GamChipVariant.DEFAULT,
      leftIcon: icon,
    };
  }

  static isLocationDenied(code?: number): boolean {
    return code === GeolocationPositionError.PERMISSION_DENIED;
  }

  static isPositionInvalid(code?: number): boolean {
    return code === GeolocationPositionError.POSITION_UNAVAILABLE;
  }
  static isTimeout(code?: number): boolean {
    return code === GeolocationPositionError.TIMEOUT;
  }

  static mapMarkers(pins?: InstanceType<typeof CustomMarker>[]): any[] {
    if (!pins) return [];
    return pins.map((pin) => {
      return pin.customMarker;
    });
  }

  static redirectToGoogleMaps(loc?: GeoCoords): void {
    const url = `https://www.google.com/maps/?q=${loc?.lat},${loc?.lng}`;
    window.open(url, '_blank');
  }

  static mapSearchLocations(data?: SearchLocationDto[]): GamDropdownItem[] {
    if (!data) return [];
    return data.map((location: SearchLocationDto) => {
      return {
        id: location.id,
        value: location.id,
        accuracy: location.accuracy || 'unknown',
        label: location.label,
        subLabel: location.subLabel,
        name: location.name,
        address: location.address,
        nearbyPlaceAddress: location.nearbyPlaceAddress,
        googlePlaceId: location.googlePlaceId,
        icon:
          location.source === MapLocationSource.INTERNAL
            ? this.getLocationTypeIcon(location.type || LocationType.CHESS_FRIENDLY)
            : GamIconName.LOCATION_EMPTY,
        coordinates: location.coordinates,
        source: location.source,
        isFullSize: true,
        chessLocation: false,
        // @ts-ignore
        sessionToken: location.sessionToken,
      } as GamDropdownItem;
    });
  }

  static isAddMode(): boolean {
    const mapStore = useMapStore();
    // TODO: Infer add mode from URL
    // Makle sure there aren't other places that rely on this state variable
    const { addMode } = storeToRefs(mapStore);

    const tableStore = useTableStore<GamDropdownItem>(GamListId.ADD_LOCATION)();
    const { selectedItem } = storeToRefs(tableStore);

    return !(!addMode.value && !selectedItem.value);
  }

  static selectedLocation(): GamDropdownItem | null {
    const tableStore = useTableStore<GamDropdownItem>(GamListId.LOCATIONS)();
    const { selectedItem } = storeToRefs(tableStore);

    return selectedItem.value;
  }

  static selectedAddLocation(): GamDropdownItem | null {
    const addTableStore = useTableStore<GamDropdownItem>(GamListId.ADD_LOCATION)();
    const { selectedItem } = storeToRefs(addTableStore);

    return selectedItem.value;
  }

  static selectedSearchLocation(): GamDropdownItem | null {
    const searchTableStore = useTableStore<GamDropdownItem>(GamListId.LOCATION_SEARCH)();
    const { selectedItem } = storeToRefs(searchTableStore);
    return selectedItem.value;
  }

  static isValidDDFormat(coordinateString: string): boolean {
    const [latitude, longitude] = coordinateString.split(',');
    const lat = parseFloat(latitude?.trim());
    const lon = parseFloat(longitude?.trim());
    return !isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
  }

  static parseSearchToCoordinates(query?: string): GeoCoords | undefined {
    if (query && this.isValidDDFormat(query)) {
      const splitText = query.split(',');
      const coordArray = splitText.map((entry) => Number(entry));

      return {
        lat: coordArray[1],
        lng: coordArray[0],
      };
    }
    return undefined;
  }

  static getAddress(selectedItem: GamDropdownItem | null): string | undefined {
    if (selectedItem?.subLabel && !this.isValidDDFormat(selectedItem.label)) {
      return `${selectedItem.label}, ${selectedItem.subLabel}`;
    }
    return selectedItem?.label;
  }

  static async authAuthor(): Promise<AuthorDto> {
    const user = await gambits.userService.getUserInfo();
    return {
      id: user.data?.id || '',
      username: user.data?.username || '',
      emoji: user.data?.emoji,
      profileImageBlobHash: user.data?.profileImageBlobHash || null,
    };
  }

  static roundLocation(loc?: GeoCoords, fraction: number = 2): GeoCoords {
    if (typeof loc?.lat !== 'number' || typeof loc?.lng !== 'number') {
      return { lat: NaN, lng: NaN };
    }

    return {
      lat: parseFloat(loc.lat.toFixed(fraction)),
      lng: parseFloat(loc.lng.toFixed(fraction)),
    };
  }

  static shouldUpdateUserLocation(oldLoc?: GeoCoords, newLoc?: GeoCoords): boolean {
    if (!oldLoc || !newLoc) return true;
    return !isEqual(this.roundLocation(oldLoc), this.roundLocation(newLoc));
  }
}
